import React, { ReactElement, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { Container } from '../components/Animations'
import ConfirmButtonModal from '../components/ConfirmButtonModal'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Dots from '../icons/Dots'
import Table from '../components/Table'
import Radom, { IPermissionRole } from '../state/Radom'
import { RADOM_COLORS } from '../util/Constants'
import { errorToast, successToast } from '../util/Util'
import { PermissionView } from './Team/APIRoles'
import {
  SettingsCard,
  SettingsCardDescription,
  SettingsCardHeader,
  SettingsCardTitle,
  SettingsHero,
  SettingsPage,
  SettingsSubtext,
  SettingsTitle,
  StatusPill
} from './Organization/SettingsLayout'

const ADMIN_ROLE_ID = '00000000-0000-0000-0000-000000000000'
const READONLY_ROLE_ID = '00000000-0000-0000-0000-000000000001'

const RolePill = styled.span`
  background: ${RADOM_COLORS.GRAY_MED};
  border-radius: 6px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  white-space: nowrap;
`

const UserCountBadge = styled.span`
  background: ${RADOM_COLORS.GRAY_MED};
  border-radius: 20px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: 11px;
  font-weight: 500;
  padding: 2px 9px;
`

const SecondaryButton = styled.button`
  align-items: center;
  background: black;
  border: 1px solid #e3e5ef;
  border-radius: 10px;
  color: white;
  cursor: pointer;
  display: inline-flex;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 465;
  gap: 6px;
  min-height: 36px;
  padding: 0 14px;
  transition: 0.14s ease all;
  white-space: nowrap;

  :hover {
    background: ${RADOM_COLORS.GRAY_MED};
    border-color: #d4d8e8;
  }

  :active {
    opacity: 0.85;
  }
`

const RolesSettings = observer((): ReactElement => {
  const navigate = useNavigate()
  const [roles, setRoles] = useState([] as IPermissionRole[])
  const [isLoadingRoles, setIsLoadingRoles] = useState(true)

  const listRoles = (): void => {
    setIsLoadingRoles(true)
    Radom.listPermissionRoles()
      .then(r => setRoles(r))
      .finally(() => setIsLoadingRoles(false))
  }

  useEffect(() => {
    listRoles()
  }, [])

  return (
    <Container>
      <SettingsPage>

        <SettingsHero>
          <div>
            <SettingsTitle>Roles</SettingsTitle>
            <SettingsSubtext>
              Define what each role can read or modify in your organization.
            </SettingsSubtext>
          </div>
          {roles.length > 0 && (
            <StatusPill $tone='neutral'>{roles.length} {roles.length === 1 ? 'role' : 'roles'}</StatusPill>
          )}
        </SettingsHero>

        <SettingsCard>
          <SettingsCardHeader>
            <div>
              <SettingsCardTitle>
                Permission roles
                {roles.length > 0 && (
                  <UserCountBadge style={{ marginLeft: 8 }}>{roles.length}</UserCountBadge>
                )}
              </SettingsCardTitle>
              <SettingsCardDescription>
                Assign roles to team members to control what they can access.
              </SettingsCardDescription>
            </div>
            <SecondaryButton onClick={() => navigate('/role/create')}>
              <span style={{ fontSize: 14, lineHeight: 1 }}>+</span>
              <span>Create role</span>
            </SecondaryButton>
          </SettingsCardHeader>

          <Table
            mergeHeader
            borderless
            disableHoverStyle
            disableOnClickState
            headers={['Role name', 'Members', 'Permissions', ''].map(h => <span key={h}>{h}</span>)}
            items={
              roles.map(r => {
                const memberCount = r.users.length
                const isBuiltIn = r.id === ADMIN_ROLE_ID || r.id === READONLY_ROLE_ID

                return [
                  <div key="roleName" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#20242d', fontSize: 13, fontWeight: 500 }}>{r.name}</span>
                    {isBuiltIn && (
                      <StatusPill $tone='neutral' style={{ fontSize: 10 }}>Built-in</StatusPill>
                    )}
                  </div>,
                  <div key="users" style={{ display: 'flex', alignItems: 'center' }}>
                    {memberCount > 0
                      ? <UserCountBadge>{memberCount} {memberCount === 1 ? 'member' : 'members'}</UserCountBadge>
                      : <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 13 }}>—</span>}
                  </div>,
                  <div key="permissions" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {r.id === ADMIN_ROLE_ID && (
                      <RolePill style={{ background: '#e7ecff', color: '#2a50c8' }}>All permissions</RolePill>
                    )}
                    {r.id === READONLY_ROLE_ID && (
                      <RolePill>Read only</RolePill>
                    )}
                    {!isBuiltIn && <PermissionView permissions={r.permissions} />}
                  </div>,
                  <div key="actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {!isBuiltIn && (
                      <Dropdown
                        key={r.id}
                        noChevron
                        iconTrigger
                        selectedContent={<Dots width={15} />}
                        onCloseFn={() => {}}
                        dropdownContent={
                          <div style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                            <ConfirmButtonModal
                              title='Delete role'
                              description='Are you sure you want to delete this role? All team members with this role will lose access.'
                              action='Delete role'
                              onConfirm={async () => {
                                Radom.deletePermissionRole(r.id)
                                  .then(() => {
                                    successToast('Role deleted')
                                    listRoles()
                                  })
                                  .catch(err => {
                                    errorToast(err.response?.data?.error || err.message)
                                  })
                              }}
                              ActionComponent={(props: React.HTMLAttributes<HTMLDivElement>) =>
                                <DropdownItem {...props} style={{ color: RADOM_COLORS.ERROR }}>
                                  Delete role
                                </DropdownItem>
                              }
                            />
                          </div>
                        }
                      />
                    )}
                  </div>
                ]
              })
            }
            isLoading={isLoadingRoles}
            emptyPlaceholderText='No custom roles yet'
            onClickRow={() => {}}
          />
        </SettingsCard>

      </SettingsPage>
    </Container>
  )
})

export default RolesSettings
